// Display-only phone formatting. Never throws: anything it does not recognise comes back as the
// trimmed original text, and empty/missing input comes back as ''.
//
//   4161111111            -> (416) 111-1111
//   14161111111           -> (416) 111-1111
//   +1 (416) 111-1111     -> (416) 111-1111
//   780-624-2443 ext 107  -> (780) 624-2443 ext. 107
//   +44 20 7946 0958      -> +44 20 7946 0958   (left as typed)
//   abc / 12345           -> abc / 12345        (left as typed)
const EXTENSION = /(?:\b(?:ext|extension|x)\.?|#)\s*(\d{1,6})\s*$/i;

const formatPhone = (value: unknown): string => {
  try {
    if (value === null || value === undefined) return '';
    const raw = String(value).trim();
    if (!raw) return '';

    let main = raw;
    let extension = '';
    const extMatch = raw.match(EXTENSION);
    if (extMatch) {
      [, extension] = extMatch;
      main = raw.slice(0, extMatch.index).trim();
    }

    // Only a plain North American number gets reformatted; anything with letters or another
    // country code is shown as typed so we never mangle it.
    if (/[a-z]/i.test(main)) return raw;
    const hasPlus = main.startsWith('+');
    let digits = main.replace(/\D/g, '');
    if (hasPlus && !digits.startsWith('1')) return raw;
    if (digits.length === 11 && digits.startsWith('1'))
      digits = digits.slice(1);
    if (digits.length !== 10) return raw;

    const formatted = `(${digits.slice(0, 3)}) ${digits.slice(
      3,
      6,
    )}-${digits.slice(6)}`;
    return extension ? `${formatted} ext. ${extension}` : formatted;
  } catch {
    return typeof value === 'string' ? value : '';
  }
};

// tel: link for the same value, using formatPhone's acceptance rule: one North American
// number (10 digits, or 11 starting with 1) or an explicit +country number, extension
// dropped. '' for anything else so the caller falls back to plain text.
export const phoneHref = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const main = String(value).replace(EXTENSION, '').trim();
  if (!main || /[a-z]/i.test(main)) return '';
  const plus = main.startsWith('+') ? '+' : '';
  const digits = main.replace(/\D/g, '');
  const northAmerican =
    digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  if (!plus && !northAmerican) return '';
  return digits.length >= 7 ? `tel:${plus}${digits}` : '';
};

// As-you-type mask for a phone input. Returns what to store (what the API gets) and what to
// show. A plain North American number is stored as its 10 digits and shown as "(416) 555-0100"
// while it is typed; an extension after it is kept as typed ("4165550100 ext 107" stored,
// "(416) 555-0100 ext 107" shown); anything else, another country's number or letters, is
// stored and shown exactly as typed. Nothing is ever lost or invented.
export const maskPhoneInput = (
  typed: string,
  previousDisplay = '',
): { value: string; display: string } => {
  const text = typed.replace(/^\s+/, '');
  if (!text) return { value: '', display: '' };
  if (text.startsWith('+')) return { value: text.trim(), display: text };

  const split = (t: string) => {
    const at = t.search(/[a-z#*]/i);
    return at === -1
      ? { head: t, tail: '' }
      : { head: t.slice(0, at), tail: t.slice(at).replace(/^\s+/, '') };
  };
  const { head, tail } = split(text);
  const previous = split(previousDisplay);
  let digits = head.replace(/\D/g, '');

  // Backspace over a formatting character: the digits did not change, so drop one.
  if (
    text.length < previousDisplay.length &&
    digits === previous.head.replace(/\D/g, '') &&
    tail === previous.tail &&
    digits.length > 0
  ) {
    digits = digits.slice(0, -1);
  }
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length > 10) return { value: text.trim(), display: text };
  if (!digits) return { value: tail, display: tail };

  let formatted = `(${digits}`;
  if (digits.length > 3)
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length > 6) {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6,
    )}`;
  }
  // The tail keeps its trailing space while it is being typed ("ext " then "107").
  return {
    value: tail ? `${digits} ${tail}`.trimEnd() : digits,
    display: tail ? `${formatted} ${tail}` : formatted,
  };
};

export default formatPhone;
